import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api/client.js';

// Post-payment welcome surface (ported from postpay-pack). Rendered as a portal
// OVER the Settings billing-success return so the billing tab + its entitlements
// re-fetch stay mounted behind it — the payment path is never touched. One
// surface, three outcomes the SPA already knows: a research run is running
// (the webhook usually auto-starts it) → "on its way"; a report is ready →
// "up to date"; nothing yet → a minimal "add your brand" intake that starts it.
//
// Note: state 02 feeds the typed website/description straight into the research
// INTAKE (not the brand profile). The brand-profile PUT is a full-replace upsert
// that requires businessDescription+targetAudience and resets omitted fields, so
// a partial save here would be unsafe — and the user hitting this state has no
// profile to satisfy it anyway. The research run reads the website regardless.

const ACTIVE_STATUSES = ['queued', 'gathering', 'analyzing', 'synthesizing', 'verifying'];

function unwrap(res) {
  return res && res.data && typeof res.data === 'object' ? res.data : res;
}

function firstSentence(s) {
  return String(s || '').split(/[.!?\n]/)[0].trim().slice(0, 120);
}

// Mirrors ResearchTab.buildIntake (the proven manual-run shape), with optional
// overrides from the typed website/description. Absent fields are stripped.
function buildIntake(profile, override) {
  const p = profile || {};
  const description = String((override && override.businessDescription) ?? p.businessDescription ?? '').trim();
  const seed = firstSentence(description) || p.businessName || '';
  const tz = String(p.timezone || '');
  const tzCity = tz.includes('/') ? tz.split('/').pop().replace(/_/g, ' ') : '';
  const intake = {
    business: seed || undefined,
    brandName: p.businessName || undefined,
    businessDescription: description || undefined,
    productOrService: p.productOrService || undefined,
    targetAudience: p.targetAudience || undefined,
    offer: p.offer || undefined,
    positioning: p.positioning || undefined,
    uniqueSellingPoints: p.uniqueSellingPoints && p.uniqueSellingPoints.length ? p.uniqueSellingPoints : undefined,
    buyerPains: p.buyerPains && p.buyerPains.length ? p.buyerPains : undefined,
    website: ((override && override.website) ?? p.websiteUrl) || undefined,
    geography: tzCity || undefined,
    timezone: p.timezone || undefined,
    language: p.language || undefined,
  };
  return Object.fromEntries(Object.entries(intake).filter(([, v]) => v !== undefined));
}

function formatDate(value, lang) {
  const ts = Date.parse(value || '');
  if (!Number.isFinite(ts)) return '';
  try {
    return new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(new Date(ts));
  } catch {
    return '';
  }
}

const Spark = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 13.8 8.2 19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 4v3M20.5 5.5h-3" strokeWidth="1.6" /></svg>
);
const Shield = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="M9.5 12.5 11 14l3.5-3.8" strokeWidth="2.2" /></svg>
);
const ChartUp = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3.5 13.5 7 17l4-5 3 3.5L17.5 9 21 13" /><path d="M3 20h18" strokeWidth="1.6" /></svg>
);
const Search = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
);
const DocLines = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 13h6M9 17h4" /></svg>
);
const Arrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h12M11 8l4 4-4 4" /><path d="M21 4v16" strokeWidth="1.6" /></svg>
);
const Clock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);
const Check = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="m5 12 5 5 9-11" /></svg>
);
const DocOpen = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
);

export default function PostPayWelcome({ onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading'); // loading | running | done | ask
  const [profile, setProfile] = useState(null);
  const [doneInfo, setDoneInfo] = useState(null);
  const [website, setWebsite] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const webRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Resolve the outcome once. If we can't even read the research list we bail to
  // the (untouched) billing page rather than guess — a fail-safe, never a wrong
  // state. The profile read is best-effort.
  useEffect(() => {
    let alive = true;
    (async () => {
      let listRes;
      try {
        listRes = await api.research.list();
      } catch {
        if (alive) closeRef.current && closeRef.current();
        return;
      }
      let prof = null;
      try {
        prof = unwrap(await api.profile.getBrandProfile())?.brandProfile || null;
      } catch { /* best-effort */ }
      if (!alive) return;
      setProfile(prof);
      const reports = unwrap(listRes)?.reports || [];
      const latest = reports[0] || null;
      if (latest && ACTIVE_STATUSES.includes(latest.status)) {
        setPhase('running');
        return;
      }
      // A finished DEEP report → genuinely up to date (the re-subscribe case).
      const readyDeep = reports.find((r) => r && r.status === 'ready' && r.depth !== 'lite');
      if (readyDeep) {
        setDoneInfo({ version: readyDeep.version, date: readyDeep.completedAt || readyDeep.createdAt });
        setPhase('done');
        return;
      }
      // Only a lite report (from free onboarding) and no active run: right after
      // payment the webhook starts a deep run server-side from the inherited
      // intake, so read this as "on its way" rather than a misleading "up to date".
      if (reports.some((r) => r && r.status === 'ready')) {
        setPhase('running');
        return;
      }
      if (prof && prof.websiteUrl) setWebsite(String(prof.websiteUrl).replace(/^https?:\/\//, ''));
      if (prof && prof.businessDescription) setDesc(prof.businessDescription);
      setPhase('ask');
    })();
    return () => { alive = false; };
  }, []);

  // Focus the website field when the intake appears.
  useEffect(() => {
    if (phase === 'ask' && webRef.current) webRef.current.focus();
  }, [phase]);

  const goDashboard = useCallback(() => { onClose && onClose(); navigate('/app'); }, [navigate, onClose]);
  const goResearch = useCallback(() => { onClose && onClose(); navigate('/app/research'); }, [navigate, onClose]);

  // Escape always takes the no-guilt path to the dashboard.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') goDashboard(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [goDashboard]);

  const startResearch = useCallback(async () => {
    setErr('');
    const w = website.trim();
    const d = desc.trim();
    if (!w && !d) return;
    setBusy(true);
    try {
      const normalizedWebsite = w ? (w.startsWith('http') ? w : `https://${w}`) : undefined;
      const intake = buildIntake(profile, { website: normalizedWebsite, businessDescription: d || undefined });
      await api.research.create({ trigger: 'manual', language: i18n.language, intake });
      setPhase('running');
    } catch {
      setErr(t('postpay.startError'));
    } finally {
      setBusy(false);
    }
  }, [website, desc, profile, i18n.language, t]);

  const brandName = (profile && profile.businessName) || '';
  const monogram = (brandName || 'B').trim().charAt(0).toUpperCase();

  const top = (
    <div className="ppw-top">
      <div className="ppw-logo" aria-hidden="true">{monogram}</div>
      <div className="ppw-bn">
        <div className="ppw-nm">{brandName || t('postpay.yourBrand')}</div>
        <div className="ppw-pl">{t('postpay.planActive')}</div>
      </div>
      <span className="ppw-paid"><Check />{t('postpay.paid')}</span>
    </div>
  );

  let body;
  if (phase === 'loading') {
    body = <div className="ppw-loading"><span className="ppw-dot" aria-hidden="true" /></div>;
  } else if (phase === 'running') {
    body = (
      <div className="ppw-body">
        <div className="ppw-mark ppw-spark" aria-hidden="true"><Spark /></div>
        <h1 className="ppw-h">{t('postpay.runningTitle')}</h1>
        <p className="ppw-sub">{t('postpay.runningSub')}</p>
        <div className="ppw-what">
          <span className="ppw-ic"><Search /></span>
          <p dangerouslySetInnerHTML={{ __html: t('postpay.runningWhat') }} />
        </div>
        <div className="ppw-meta">
          <span className="ppw-eta"><Clock />{t('postpay.eta')}</span>
          <span className="ppw-chip" role="status" aria-live="polite">
            <span className="ppw-dot" aria-hidden="true" />
            <span className="ppw-l">{t('postpay.chipLabel')}<b>· {t('postpay.chipRunning')}</b></span>
          </span>
        </div>
        <div className="ppw-acts">
          <button type="button" className="ppw-btn ppw-btn-ghost" onClick={goDashboard}>{t('postpay.dashboard')}</button>
          <button type="button" className="ppw-btn ppw-btn-primary" onClick={goResearch}><Arrow />{t('postpay.seeProgress')}</button>
        </div>
      </div>
    );
  } else if (phase === 'done') {
    body = (
      <div className="ppw-body">
        <div className="ppw-mark is-done" aria-hidden="true"><ChartUp /></div>
        <h1 className="ppw-h">{t('postpay.doneTitle')}</h1>
        <p className="ppw-sub">{t('postpay.doneSub')}</p>
        <div className="ppw-what is-done">
          <span className="ppw-ic"><DocLines /></span>
          <p>{doneInfo && doneInfo.version != null
            ? t('postpay.doneReportVersioned', { version: doneInfo.version, date: formatDate(doneInfo.date, i18n.language) })
            : t('postpay.doneReport')}</p>
        </div>
        <div className="ppw-acts">
          <button type="button" className="ppw-btn ppw-btn-ghost" onClick={goDashboard}>{t('postpay.dashboard')}</button>
          <button type="button" className="ppw-btn ppw-btn-primary" onClick={goResearch}><DocOpen />{t('postpay.openResearch')}</button>
        </div>
      </div>
    );
  } else {
    // ask
    body = (
      <div className="ppw-body">
        <div className="ppw-mark" aria-hidden="true"><Shield /></div>
        <h1 className="ppw-h">{t('postpay.askTitle')}</h1>
        <p className="ppw-sub">{t('postpay.askSub')}</p>
        <div className="ppw-field">
          <label htmlFor="ppw-web">{t('postpay.websiteLabel')}</label>
          <div className="ppw-inp">
            <span className="ppw-pre">https://</span>
            <input
              id="ppw-web"
              ref={webRef}
              type="text"
              autoComplete="off"
              placeholder={t('postpay.websitePlaceholder')}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') startResearch(); }}
            />
          </div>
          <div className="ppw-hint"><Search />{t('postpay.websiteHint')}</div>
        </div>
        <div className="ppw-orline">{t('postpay.or')}</div>
        <div className="ppw-field">
          <label htmlFor="ppw-desc">{t('postpay.descLabel')} <span className="ppw-opt">{t('postpay.optional')}</span></label>
          <div className="ppw-inp">
            <input
              id="ppw-desc"
              type="text"
              placeholder={t('postpay.descPlaceholder')}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') startResearch(); }}
            />
          </div>
        </div>
        {err && <div className="ppw-err" role="alert">{err}</div>}
        <div className="ppw-acts">
          <button type="button" className="ppw-btn ppw-btn-ghost" onClick={goDashboard} disabled={busy}>{t('postpay.later')}</button>
          <button
            type="button"
            className="ppw-btn ppw-btn-primary"
            onClick={startResearch}
            disabled={busy || (!website.trim() && !desc.trim())}
          >
            {busy ? <span className="ppw-dot" aria-hidden="true" /> : <Arrow />}
            {t('postpay.startResearch')}
          </button>
        </div>
      </div>
    );
  }

  return createPortal(
    <div className="ppw-scene">
      <div className="ppw-sheet" role="dialog" aria-modal="true" aria-label={t('postpay.ariaLabel')}>
        {top}
        {body}
      </div>
    </div>,
    document.body
  );
}
